import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ObligationDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type {
  AssessmentResultParentWithObligationResultsResponseRow,
  ObligationRegisterResponseRow,
} from '@risksmart-app/drizzle/src/types';
import type { ObligationRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  GetObligationsQuery,
  GetObligationsQueryVariables,
  Obligation_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetObligationsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import { OBLIGATION_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  type FullResultHistoryArg,
  type ResultHistoryWindowArg,
  tagsWhereOnEntity,
  toFullResultHistoryVariables,
  toResultHistoryVariables,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';

const mapTrpcAssessmentResultParentToGraphQL = (
  assessment_result_parent: AssessmentResultParentWithObligationResultsResponseRow
) => ({
  ...assessment_result_parent,
});

const mapTrpcObligationToGraphQL = (
  obligation: ObligationRegisterResponseRow
) => ({
  ...obligation,
  Parent: obligation.parent,
  BreachedIssues: obligation.issues,
  CreatedByUser: obligation.CreatedByUser,
  ModifiedByUser: obligation.ModifiedByUser,
  CreatedBy: obligation.createdBy,
  ModifiedBy: obligation.modifiedBy,
  controls_aggregate: {
    aggregate: {
      count: obligation.controls.length,
    },
  },
});

const useObligationsGraphqlVariables = (
  args: UseGetObligationsRegisterArgs
) => {
  const { user } = useRisksmartUser();

  // Explicit type arguments: obligation has no `base` (its own predicate is
  // `undefined`), so there is nothing to anchor `TWhere` for inference — see
  // the oversight hook's identical comment.
  const { where } = buildDashboardWhere<
    DashboardFilterArgs<ObligationDashboardDateField>,
    Obligation_Bool_Exp
  >({
    filters: args,
    userId: user?.userId,
    datePredicates: OBLIGATION_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.obligationFilterConditions,
  });

  const resultHistoryVariables =
    toResultHistoryVariables<GetObligationsQueryVariables>(
      args.resultHistoryWindow,
      (bounds) => ({ obligationAssessmentResultsWhere: { TestDate: bounds } })
    );

  // COI-838 — OMITTED unless the caller opts out of the cap. The document
  // defaults `$assessmentResultsLimit` to 2, and a GraphQL default applies only
  // to an ABSENT variable — so this must contribute nothing in the common case
  // and `{ assessmentResultsLimit: null }` for the two rating charts.
  const fullResultHistoryVariables =
    toFullResultHistoryVariables<GetObligationsQueryVariables>(
      args.fullResultHistory,
      () => ({ assessmentResultsLimit: null })
    );

  return {
    ...resultHistoryVariables,
    ...fullResultHistoryVariables,
    ...(where ? { where } : {}),
  };
};

export type UseGetObligationsRegisterArgs =
  DashboardFilterArgs<ObligationDashboardDateField> &
    ResultHistoryWindowArg &
    FullResultHistoryArg;

export const useGetObligationsRegister = createQueryHook<
  UseGetObligationsRegisterArgs,
  ObligationRegisterResponse,
  GetObligationsQuery
>({
  trpcQueryOptions: (
    trpc,
    {
      tagTypeIds,
      departmentTypeIds,
      dateRange,
      ownership,
      resultHistoryWindow,
      fullResultHistory,
    }
  ) =>
    trpc.frontend.obligation.register.queryOptions(
      toTrpcDashboardFilterInput({
        tagTypeIds,
        departmentTypeIds,
        dateRange,
        ownership,
        resultHistoryWindow,
        fullResultHistory,
      })
    ),
  mapTrpcDataToGraphQL: (data) => ({
    obligation: data.obligation.map(mapTrpcObligationToGraphQL),
    assessment_result_parent: data.assessment_result_parent.map(
      mapTrpcAssessmentResultParentToGraphQL
    ),
  }),
  graphqlDocument: GetObligationsDocument,
  graphqlVariables: useObligationsGraphqlVariables,
});
